import React, { useState, useEffect, useMemo } from 'react';
import { useAppSettings, CosmosContributor, CosmosRepo } from '../context/AppSettingsContext';

const SettingsPanel: React.FC = () => {
  const { settings, setSettings, cosmosLogos } = useAppSettings();

  const [githubUser, setGithubUser] = useState(settings.githubUser);
  const [consciousness, setConsciousness] = useState(settings.consciousness);
  const [neuralpathway, setNeuralpathway] = useState(settings.neuralpathway);
  const [basePath, setBasePath] = useState(settings.basePath);
  const [enableAudio, setEnableAudio] = useState(settings.enableAudio);
  const [disableMarkdown, setDisableMarkdown] = useState(settings.disableMarkdown);
  const [debug, setDebug] = useState(settings.debug);
  const [connectionStatus, setConnectionStatus] = useState<null | 'success' | 'fail'>(null);

  const contributors = useMemo(() => cosmosLogos?.contributors || [], [cosmosLogos]);
  const [selectedUser, setSelectedUser] = useState<CosmosContributor | null>(null);
  const [validRepos, setValidRepos] = useState<CosmosRepo[]>([]);

  useEffect(() => {
    const user = contributors.find((c) => c.github === githubUser) || null;
    setSelectedUser(user);
    const repos = user?.repos || [];

    setValidRepos(repos);
    if (repos.length > 0 && !repos.some((r) => r.repo === consciousness)) {
      setConsciousness(repos[0].repo);
    }
  }, [githubUser, contributors, consciousness]);

  useEffect(() => {
    if (!cosmosLogos || !selectedUser) return;
    const repo = selectedUser.repos.find((r) => r.repo === consciousness);
    if (!repo) return;

    setNeuralpathway((prev) => repo.default_branch || cosmosLogos.default_branch || prev);
    setBasePath(repo.base_path || 'source_of_truth');
  }, [consciousness, selectedUser, cosmosLogos]);

  const applyChanges = () => {
    const confirmed = window.confirm(
      'Apply new runtime settings?\n\nThis will refresh the viewer to the root.'
    );
    if (!confirmed) return;

    setSettings({
      githubUser,
      consciousness,
      neuralpathway,
      basePath,
      enableAudio,
      disableMarkdown,
      debug,
    });

    const params = new URLSearchParams();
    params.set('debug', String(debug));
    params.set('user', githubUser);
    params.set('consciousness', consciousness);
    params.set('neuralpathway', neuralpathway);
    params.set('base_path', basePath);
    params.set('audio', String(enableAudio));
    params.set('disableMarkdown', String(disableMarkdown));

    const url = `/writings?${params.toString()}`;
    window.location.href = url;
  };

  const testConnectivity = async () => {
    const parts = [
      'https://raw.githubusercontent.com',
      githubUser,
      consciousness,
      neuralpathway,
      basePath,
      'cosmos-logos.json',
    ];

    const url = parts.map((p) => p.replace(/^\/|\/$/g, '')).join('/');
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('404');
      await res.json();
      setConnectionStatus('success');
    } catch {
      setConnectionStatus('fail');
    }
  };

  return (
    <div style={{ padding: '0.5rem 0', color: '#ccc' }}>
      {/* GitHub User */}
      <div style={{ marginBottom: '0.75rem' }}>
        <label style={{ display: 'block', fontWeight: 'bold' }}>GitHub User</label>
        {contributors.length > 0 ? (
          <select
            value={githubUser}
            onChange={(e) => setGithubUser(e.target.value)}
            style={{ width: '100%', background: '#222', color: '#fff', border: '1px solid #555' }}
          >
            {contributors.map((contributor) => (
              <option key={contributor.github} value={contributor.github}>
                {contributor.github}
              </option>
            ))}
          </select>
        ) : (
          <input
            value={githubUser}
            onChange={(e) => setGithubUser(e.target.value)}
            style={{ width: '100%', background: '#222', color: '#fff', border: '1px solid #555' }}
          />
        )}
      </div>

      {/* Consciousness */}
      <div style={{ marginBottom: '0.75rem' }}>
        <label style={{ display: 'block', fontWeight: 'bold' }}>Consciousness (Repo)</label>
        {validRepos.length > 0 ? (
          <select
            value={consciousness}
            onChange={(e) => setConsciousness(e.target.value)}
            style={{ width: '100%', background: '#222', color: '#fff', border: '1px solid #555' }}
          >
            {validRepos.map((repo) => (
              <option key={repo.repo} value={repo.repo}>
                {repo.repo}
              </option>
            ))}
          </select>
        ) : (
          <input
            value={consciousness}
            onChange={(e) => setConsciousness(e.target.value)}
            placeholder="Enter repo name"
            style={{ width: '100%', background: '#222', color: '#fff', border: '1px solid #555' }}
          />
        )}
      </div>

      {/* Neural Pathway */}
      <div style={{ marginBottom: '0.75rem' }}>
        <label style={{ display: 'block', fontWeight: 'bold' }}>Neural Pathway (Branch)</label>
        <input
          value={neuralpathway}
          onChange={(e) => setNeuralpathway(e.target.value)}
          style={{ width: '100%', background: '#222', color: '#fff', border: '1px solid #555' }}
        />
      </div>

      {/* Base Path */}
      <div style={{ marginBottom: '0.75rem' }}>
        <label style={{ display: 'block', fontWeight: 'bold' }}>Source of Truth (Base Path)</label>
        <input
          value={basePath}
          onChange={(e) => setBasePath(e.target.value)}
          style={{ width: '100%', background: '#222', color: '#fff', border: '1px solid #555' }}
        />
      </div>

      {/* Debug Toggle */}
      <div style={{ marginTop: '0.75rem' }}>
        <label style={{ display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={debug}
            onChange={(e) => setDebug(e.target.checked)}
            style={{ marginRight: '0.5rem' }}
          />
          Enable Debug Mode
        </label>
      </div>

      {/* Audio Toggle */}
      <div style={{ marginTop: '0.75rem' }}>
        <label style={{ display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={enableAudio}
            onChange={(e) => setEnableAudio(e.target.checked)}
            style={{ marginRight: '0.5rem' }}
          />
          Enable Audio Narration
        </label>
      </div>

      {/* Disable Markdown Toggle */}
      <div style={{ marginTop: '0.5rem' }}>
        <label style={{ display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={disableMarkdown}
            onChange={(e) => setDisableMarkdown(e.target.checked)}
            style={{ marginRight: '0.5rem' }}
          />
          Disable Markdown Styling
        </label>
      </div>

      {/* Connectivity */}
      <button
        onClick={testConnectivity}
        style={{
          marginTop: '1rem',
          background: '#333',
          color: '#ffdd80',
          padding: '0.5rem 1rem',
          border: '1px solid #555',
          cursor: 'pointer',
          fontWeight: 'bold',
          borderRadius: '4px',
        }}
      >
        Test Connectivity
      </button>

      {connectionStatus === 'success' && (
        <p style={{ color: '#80ffb0', marginTop: '0.5rem' }}>✅ Connected</p>
      )}
      {connectionStatus === 'fail' && (
        <p style={{ color: '#ff6b6b', marginTop: '0.5rem' }}>❌ cosmos-logos.json not found</p>
      )}

      <button
        onClick={applyChanges}
        style={{
          marginTop: '1rem',
          background: '#ffdd80',
          color: '#111',
          padding: '0.5rem 1rem',
          border: 'none',
          cursor: 'pointer',
          fontWeight: 'bold',
          borderRadius: '4px',
        }}
      >
        Apply & Restart
      </button>
    </div>
  );
};

export default SettingsPanel;
